/* ============================================================
   Implementation gates — running the setup, not just listing it.

   The Licensing Navigator answers "what must I obtain". It stops
   there, which leaves the operator holding a list and no sequence.
   Every question that follows is a delivery question: in what order,
   what does each one unblock, and what is it costing me to wait.

   THE DIFFERENCE FROM A GENERIC PROJECT PLAN, and the reason this file
   is organised around dependencies rather than dates:

   An aviation venture answers to one authority. A Kenyan logistics
   venture answers to six or seven — the Business Registration Service,
   KRA for the PIN and for customs, NTSA, the county, KEBS, KPA, and
   the bond issuer — each with its own clock, and several of them
   requiring another's output before they will even open a file. You
   cannot hold an NTSA operator licence without a KRA PIN. You cannot
   bond a warehouse without customs registration. You cannot register a
   vehicle to a company that does not yet exist.

   So the binding constraint is never one agency's turnaround. It is
   the dependency chain, and the commonest way a setup slips a quarter
   is applying everywhere at once and discovering that half the
   applications were unopenable.

   NO DURATIONS ARE STATED HERE. Agency turnarounds move with staffing,
   with system migrations and with the political weather; the Licensing
   Navigator carries indicative ranges where it can source them, and
   this file deliberately does not repeat them. What does not rot is
   the ORDER and the DEPENDENCY, which is what a plan actually needs.
   Same convention as v5-sez, and for the same reason.
   ============================================================ */

/* Who issues what. Named because "the regulator" is not an addressee,
   and a plan that cannot name the counterparty for a step has not
   finished thinking about it. */
export const AGENCIES = {
  brs: 'Business Registration Service',
  kra: 'Kenya Revenue Authority',
  customs: 'KRA Customs & Border Control',
  ntsa: 'National Transport and Safety Authority',
  county: 'County government',
  kebs: 'Kenya Bureau of Standards',
  kpa: 'Kenya Ports Authority',
  surety: 'Bond issuer or insurer'
};

/* Gates common to every path, plus the path-specific authority gate.
   `needs` names the gate that must close first — the dependency the
   plan is actually built on. */
export const GATES = [
  {
    id: 'entity',
    n: 'G1 — Entity and tax',
    needs: null,
    agencies: ['brs', 'kra', 'county'],
    evidence: [
      'Company registered and the certificate of incorporation in hand',
      'KRA PIN issued to the company, not to a director personally',
      'VAT registration where turnover requires it',
      'County single business permit for each operating location'
    ],
    releases: 'Nothing beyond formation cost. Everything downstream depends on this and nothing should be committed before it.',
    why: 'Every other agency asks for the PIN and the certificate before it will open a file. This gate is cheap, and it is the one that determines when the rest of the programme can start.'
  },
  {
    id: 'authority',
    n: 'G2 — Operating authority',
    needs: 'entity',
    agencies: ['ntsa', 'customs', 'kra'],
    evidence: [
      'The licence for the chosen path applied for and accepted, not merely submitted',
      'Nominated postholders named where the path requires them',
      'Security or bond arranged in principle with the issuer',
      'Premises inspected where the path requires an inspection'
    ],
    releases: 'Recruitment, systems and premises deposits.',
    why: 'This is the gate that converts a registered company into an operator. Assets committed before it are assets that cannot legally earn.'
  },
  {
    id: 'assets',
    n: 'G3 — Fleet and facility',
    needs: 'authority',
    agencies: ['ntsa', 'kebs', 'county'],
    evidence: [
      'Operating authority granted in writing for the intended scope',
      'Vehicles or facility identified with inspection dates booked',
      'Insurance terms indicated against the actual assets and drivers',
      'Telematics, weighbridge or scanning equipment specified'
    ],
    releases: 'Fleet purchase or lease, facility lease, capital equipment.',
    why: 'The largest capital commitment in the programme, and the one most often made early on the assumption that the licence is a formality. It is the licence that makes the asset earn; without it the asset is a standing cost with no revenue against it.'
  },
  {
    id: 'compliance',
    n: 'G4 — Compliance and systems',
    needs: 'assets',
    agencies: ['ntsa', 'customs', 'surety', 'kebs'],
    evidence: [
      'Insurance placed and certificates issued, not quoted',
      'Bond or security executed and lodged with the issuing authority',
      'Safety management system documented with a named responsible officer',
      'Customs, transit or tracking systems integrated and tested end to end'
    ],
    releases: 'Working capital into operations.',
    why: 'The gate that is passed on paper and failed in practice. A system integrated but never tested against a live declaration is discovered at the border, with cargo on it.'
  },
  {
    id: 'first',
    n: 'G5 — First revenue movement',
    needs: 'compliance',
    agencies: ['customs', 'kpa'],
    evidence: [
      'First consignment booked with a named customer',
      'A complete document set rehearsed against the actual route',
      'Cash cycle understood — who pays, on what terms, against what proof of delivery',
      'A named escalation contact at each agency on the route'
    ],
    releases: 'The operation itself.',
    why: 'Revenue begins here, and so does the working-capital drain: the first invoice is paid long after the first cost. Model the runway from this date rather than from launch.'
  }
];

/* Concurrency is the whole point. These run alongside one another and
   each is gated by a different thing, which is why a single Gantt bar
   labelled "setup" tells an operator nothing they can act on. */
export const WORKSTREAMS = [
  { id: 'reg', n: 'Licensing and regulatory', owner: 'Founder or compliance lead', cadence: 'Weekly',
    dep: 'Held across several agencies at once, and each one waits on another’s output.', lead: true },
  { id: 'assets', n: 'Fleet and facility', owner: 'Operations lead', cadence: 'Fortnightly',
    dep: 'Gated by the operating authority. This is where the capital sits.' },
  { id: 'people', n: 'People and safety', owner: 'Operations lead', cadence: 'Weekly',
    dep: 'Drivers, safety officer and declarants. Licence classes and medicals take longer than the hiring does.', lead: true },
  { id: 'commercial', n: 'Commercial', owner: 'Founder', cadence: 'Fortnightly',
    dep: 'Started last in most setups, which is why so many launch licensed and empty.' },
  { id: 'finance', n: 'Finance and working capital', owner: 'Founder or finance lead', cadence: 'Monthly',
    dep: 'The first invoice is paid long after the first cost. Runway is measured from first movement, not from launch.' },
  { id: 'systems', n: 'Systems and data', owner: 'Operations lead', cadence: 'Fortnightly',
    dep: 'Customs, tracking and accounting. Integration is quick; testing against a live declaration is not.' }
];

/* Each is observable before the thing it predicts, which is the only
   property that makes an indicator worth collecting. */
export const INDICATORS = [
  { k: 'Applications opened versus applications accepted', freq: 'Weekly',
    predicts: 'Whether the dependency chain is being respected',
    trigger: 'Any application returned as unopenable — it was lodged out of order' },
  { k: 'Driver licence classes and medicals verified', freq: 'Weekly',
    predicts: 'Whether the fleet can legally move once it arrives',
    trigger: 'Fewer verified drivers than vehicles at G3' },
  { k: 'Capital committed against gates passed', freq: 'Monthly',
    predicts: 'Capital at risk', trigger: 'Any commitment ahead of its gate' },
  { k: 'Test declarations or trial runs completed', freq: 'Weekly from G4',
    predicts: 'Whether G5 will hold', trigger: 'No end-to-end test by the time assets arrive' },
  { k: 'Booked revenue for the first 90 days', freq: 'Monthly',
    predicts: 'Whether the operation launches loaded or empty',
    trigger: 'No named customer by G4' },
  { k: 'Cash runway measured from first movement', freq: 'Monthly',
    predicts: 'Whether the operation survives its own receivables',
    trigger: 'Under 90 days of cover at G4' }
];

export const CONTINGENCIES = [
  { when: 'An application is returned because a prerequisite is missing',
    then: 'Treat it as a dependency error rather than an agency delay. Re-check the whole chain before resubmitting: where one application was lodged out of order, others usually were too.' },
  { when: 'The operating authority is delayed past the asset commitment date',
    then: 'Hold the assets. A leased vehicle or warehouse standing idle is a fixed cost with no licence to earn against it, and a deferral is almost always cheaper than the carry.' },
  { when: 'Insurance is quoted materially above the plan',
    then: 'Read it as a risk signal, not just a cost. Insurers price the driver record, the route and the cargo; a surprising quote usually means one of the three is worse than assumed.' },
  { when: 'The first consignment clears late',
    then: 'Debrief the document set before booking the second. The first clearance is the cheapest test of the paperwork the operation will run thousands of times.' },
  { when: 'Receivables run behind the plan',
    then: 'Working capital is the binding constraint in a new logistics operation, not margin. Reforecast the runway from first movement and hold the difference before it is needed.' }
];
